import { useState, useEffect, useMemo, useRef } from "react";
import { api } from "../api";
import { manageableRoles, sectionsForRole } from "../auth/managementSections";
import {
  ROLE_COLORS, ROLE_LABELS, grantsOf, grantableAnywhere, groupsForRole,
  isGlobalAdmin, isResearcher, managedGroupIds, managedGroups, profileConfirmed,
  roleNeedsGroup,
} from "../auth/roles";
import GroupManagementTab from "./GroupManagementTab";
import AdminDocumentsPanel from "./AdminDocumentsPanel";
import QuotationsExportPanel from "./QuotationsExportPanel";
import GroupsTab from "./GroupsTab";
import SettingsTab from "./SettingsTab";
import NotificationsTab from "./NotificationsTab";
import SignInCard from "./SignInCard";
import RoleEditor from "./RoleEditor";
import AnnouncementsTab from "./AnnouncementsTab";
import AutomationTab from "./AutomationTab";
import BackupCard from "../components/BackupCard";
import { GroupBillingBlock, billedAsGroup } from "./AccountCard";
import { useAuth } from "../auth/AuthContext";
import { PortalLayout } from "../components/Portal";
import { useDisplayTimezone } from "../portalConfig";
import MessagePreview from "../components/MessagePreview";
import { G_BG, Badge, StatusBadge, Field, Input, PasswordInput, PasswordStrength, PasswordRequirements, passwordIsAcceptable, Select, Toggle, Btn, Card, Collapsible, Th, ConfirmDialog, TouchReason, cellClip, formatWhen, useSortableColumns, useColumnWidths } from "../components/Shared";

// cellClip: both tables here are tableLayout:"fixed", so a long full name or
// email would otherwise be painted over the column beside it.
const td = { padding: "12px 16px", verticalAlign: "middle", fontSize: 14, color: "#374151", ...cellClip };

// ROLE_COLORS / ROLE_LABELS / the granting rule live in auth/roles.js, mirrored
// from the backend's portal/security/rbac.py.

// Read from the audit log, so it can only be as complete as the log is.
const INVITED_NOTE = "Read from the audit log. If the group's log has been cleared or has aged out, this reads Never even for accounts that were invited — check before sending a second invitation.";

// The way in, as it is read on the row. `auth_source` is the column; these are
// what a person reads. An unknown value is shown as it is rather than hidden.
// "ldap" is a legacy value: the directory sign-in is gone and the server
// refuses such an account everywhere until it is linked, which happens by its
// address on its first Keycloak sign-in — so the row says what it waits for.
const WAY_IN = {
  local:    ["Local", "gray"],
  keycloak: ["Keycloak", "green"],
  ldap:     ["Not linked yet", "amber",
             "Will be linked on its first Keycloak sign-in with this e-mail address."],
};

// ── User table row ────────────────────────────────────────────────────────────

function UserRow({ user: u, currentUserId, groups, tz, inviting, onEdit, onToggle, onDelete, onInvite, onLocalSignIn }) {
  const group = groups.find(g => g.id === u.group_id);
  const [wayIn, wayInColor, wayInNote] = WAY_IN[u.auth_source] || [u.auth_source, "gray"];
  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      {/* The e-mail is what identifies the account since 2026-09-28, so it
          leads the row where the username used to. */}
      <td data-label="Email" style={td} title={u.email || undefined}><span style={{ fontSize: 13 }}>{u.email || <span style={{ color: "#6b7280" }}>—</span>}</span></td>
      <td data-label="Full Name" style={td} title={u.full_name || undefined}>{u.full_name || <span style={{ color: "#6b7280" }}>—</span>}</td>
      <td data-label="Role" style={td}>
        {/* One badge per grant: an account may hold operator in one group and
            group admin in another, and showing only the strongest would hide
            standing that decides what they can reach. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {grantsOf(u).map(g => (
            <Badge key={`${g.role}-${g.group_id ?? "all"}`} color={ROLE_COLORS[g.role] || "gray"}>
              {ROLE_LABELS[g.role] || g.role}
              {g.group_name ? ` · ${g.group_name}` : ""}
            </Badge>
          ))}
        </div>
      </td>
      <td data-label="Group / Projects" style={td}>
        {/* A researcher belongs to no group (2026-09-28): what places them is
            the projects they own, are assigned to or represent, as
            `list_users` names them — only those of groups this viewer may
            see. A legacy group on a `user` is not shown: it no longer means
            anything. Staff keep their group. */}
        {isResearcher(u) ? (
          u.projects?.length
            ? <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {u.projects.map(p => (
                  <span key={p.id} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 10, background: "#f3f4f6", color: "#374151" }}>{p.name}</span>
                ))}
              </div>
            : <span style={{ color: "#6b7280", fontSize: 13 }}>No projects</span>
        ) : group
          ? <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 10, background: G_BG, color: "#006630", fontWeight: 500 }}>{group.name}</span>
          : <span style={{ color: "#6b7280" }}>—</span>}
      </td>
      <td data-label="Auth" style={td}>
        <span title={wayInNote}><Badge color={wayInColor}>{wayIn}</Badge></span>
      </td>
      <td data-label="Invited" style={td}>
        {/* When the invitation last actually reached the mail server, out of the
            audit log — a send that failed writes the error and is not counted,
            so this never claims somebody was reached when they were not. Only
            GET /api/admin/users fills it; every other response leaves it null,
            which is why "Never" is only ever painted in this table. And it is
            only as complete as the log: a cleared or aged-out group log leaves
            this reading Never for accounts that were invited — INVITED_NOTE
            says so on the header. */}
        {u.invitation_sent_at
          ? <span style={{ fontSize: 13 }}>{formatWhen(u.invitation_sent_at, { zone: tz })}</span>
          : <span style={{ color: "#6b7280" }}>Never</span>}
      </td>
      <td data-label="Status" style={td}>
        {u.is_active ? <StatusBadge status="active">Active</StatusBadge> : <StatusBadge status="failed">Inactive</StatusBadge>}
        {/* Active is not the same as confirmed (2026-09-28): a self-registered
            account is active and reaches only its profile until confirmed. */}
        {!profileConfirmed(u) && (
          <div style={{ marginTop: 4 }}><Badge color="amber">Awaiting confirmation</Badge></div>
        )}
      </td>
      <td data-label="" style={{ ...td, textAlign: "right" }}>
        {/* `data-user-id` is how focus finds this row's Edit again when the
            form closes — on a phone the row is far below the form, and focus
            returning to it is what scrolls the person back to where they were. */}
        <div data-row-actions data-user-id={u.id} style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={() => onEdit(u)} style={{ padding: "5px 12px", fontSize: 12 }}>Edit</Btn>
          {/* An account is created with no password at all: the invitation is
              the only way into it, so a message that did not arrive has to be
              re-sendable. Offered on local accounts only — a directory account
              has no portal password to set — and only while the account is
              active, because the link would not work on a deactivated one.
              Those are the two states the server answers 409 for, asked here so
              the button is not offered for a refusal.

              The title sits on a wrapper, not on the button: a disabled
              <button> swallows the hover in several browsers, and Btn does not
              forward `title`. */}
          {u.auth_source === "local" && u.is_active && (
            <span
              style={{ display: "inline-flex" }}
              title={u.email
                ? "Send the welcome message again, with a fresh link to set a password"
                : "This account has no email address — Edit it first"}
            >
              <Btn
                variant="secondary"
                disabled={!u.email || inviting}
                onClick={() => onInvite(u)}
                style={{ padding: "5px 12px", fontSize: 12 }}
              >
                {inviting ? "Sending…" : "Send invitation"}
              </Btn>
            </span>
          )}
          {u.auth_source === "local" && u.is_active && !u.email && (
            <TouchReason>Send invitation: this account has no email address — Edit it first.</TouchReason>
          )}
          {/* The way back from Keycloak: the server clears the link to the
              Keycloak identity and the password hash, and the account signs in
              locally again — with no password until the person sets one through
              Send invitation, which the confirmation says before the click.
              Not offered on the admin's own row (403: it would escape the
              step-up their Keycloak session carried) nor on a deactivated
              account (409) — the refusals the server answers, asked here so the
              button is not offered for one. */}
          {u.auth_source === "keycloak" && u.is_active && u.id !== currentUserId && (
            <Btn variant="secondary" onClick={() => onLocalSignIn(u)}
                 style={{ padding: "5px 12px", fontSize: 12 }}>
              Switch back to local sign-in
            </Btn>
          )}
          {u.id !== currentUserId && (
            <>
              <Btn
                variant={u.is_active ? "danger" : "ghost"}
                onClick={() => onToggle(u)}
                style={{ padding: "5px 12px", fontSize: 12 }}
              >
                {u.is_active ? "Deactivate" : "Activate"}
              </Btn>
              <Btn
                variant="danger"
                onClick={() => onDelete(u)}
                style={{ padding: "5px 12px", fontSize: 12 }}
              >
                Delete
              </Btn>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── User form ─────────────────────────────────────────────────────────────────

// No `password`. Since 2026-09-09 an account is created without one: the
// welcome message carries a single-use link and the person sets their own, so
// there is no password for an operator to choose, to type, to remember or to
// leak. The field stays on the EDIT form, where it is a different act by a
// different person.
//
// `send_welcome_email` is true and the form offers no way to make it false —
// with no password on the form, an account nobody is told about is an account
// nobody can ever sign in to.
// The five postal fields, with the label each one wears. One list, read by the
// empty form, by the edit form's seeding, by the completeness gate and by the
// Address block itself — four places that would otherwise have to be kept in
// step by hand, which is how `invoice_email` came to be labelled *Invoicing
// address* while holding e-mail. The column keeps its name; only what people
// read has changed.
const BILLING_ADDRESS_FIELDS = [
  ["billing_name",    "Institution or company"],
  ["billing_street",  "Street"],
  ["billing_zip",     "Postcode"],
  ["billing_city",    "City"],
  ["billing_country", "Country"],
];
const BLANK_ADDRESS = Object.fromEntries(BILLING_ADDRESS_FIELDS.map(([f]) => [f, ""]));

const emptyForm = { email: "", first_name: "", last_name: "", role: "user", group_id: "", vat_id: "", ...BLANK_ADDRESS, send_welcome_email: true };

function UserForm({ initial, me, groups, onSave, onCancel }) {
  const isEdit    = !!initial;
  // Which groups this account may file a new person into. An admin may choose
  // any; everybody else only the ones they manage.
  const myGroups  = isGlobalAdmin(me) ? groups : groups.filter(g => managedGroupIds(me).includes(g.id));
  // Since 2026-09-28 the six billing keys on `initial` are the account's own
  // only for a plain `user`: a staff account's are its GROUP's, an admin's all
  // null. So the editable boxes start empty for anybody else — a demotion to
  // `user` must not open with the group's address typed in as the researcher's
  // own. The group's block is shown from `initial` itself, read-only, below.
  const ownBilling = !initial || isResearcher(initial);
  // A `user` holds no group (2026-09-28), so a legacy one on a researcher is
  // not carried into the form — it would otherwise be posted back.
  const [form, setForm]   = useState(initial ? { ...initial, password: "",
    group_id: roleNeedsGroup(initial.role) ? initial.group_id ?? "" : "",
    vat_id: ownBilling ? initial.vat_id ?? "" : "",
    // Both halves normalised to "" for the same reason as every field above: an
    // account from before the split on 2026-09-16 can have NULL in either, and
    // `undefined` turns the input into an uncontrolled one for the lifetime of
    // the form.
    first_name: initial.first_name ?? "", last_name: initial.last_name ?? "",
    ...Object.fromEntries(BILLING_ADDRESS_FIELDS.map(([f]) => [f, ownBilling ? initial[f] ?? "" : ""])) } : {
    ...emptyForm,
    // A new account starts as a `user`, which holds no group; choosing a
    // staff role fills in the one group an operator has, if they have one.
    group_id: "",
    role: "user",
  });
  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);
  // Role first, then — for a staff role only — the group it is held in. Until
  // 2026-09-28 it was the other way round, because every role needed a group;
  // a `user` needs none now, and asking for one first would ask an operator
  // for a group a researcher does not belong to.
  const set = k => e => setForm(f => {
    const next = { ...f, [k]: e.target.value };
    if (k === "role") {
      if (!roleNeedsGroup(next.role)) {
        next.group_id = "";
      } else {
        // A group in which this role cannot be granted would be refused by the
        // server; the only one there is is chosen rather than asked for.
        const allowed = groupsForRole(me, next.role, myGroups);
        if (!allowed.some(g => String(g.id) === String(next.group_id))) {
          next.group_id = allowed.length === 1 ? allowed[0].id : "";
        }
      }
    }
    return next;
  });

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = { ...form, group_id: !roleNeedsGroup(form.role) || !form.group_id ? null : Number(form.group_id) };
      // Output only: the projects `list_users` names for the row.
      delete payload.projects;
      // Derived by the server from the two halves above. The edit form is opened
      // with the whole account object, so this rides along unless it is dropped
      // — and the route ignores it, which is a silent no-op rather than an
      // error. Dropping it here says at the call site that it is not an input.
      delete payload.full_name;
      if (isEdit && !payload.password) delete payload.password;
      // Never an input — it is whether the portal's one PUMAPI key is set
      // (Portal settings, since 2026-09-28), and the edit form opens with the
      // whole account object, so it rides along unless dropped. There is no
      // `ppms_api_key` on this form any more, and `UserUpdate` has no field.
      delete payload.ppms_api_key_set;
      // The account no longer names a PPMS user (2026-10-20, the PPMS
      // catalogue): the edit form opens with the whole account object, so a
      // stored login would ride along and be posted back unless dropped.
      delete payload.ppms_login;
      delete payload.ppms_login_unverified;
      // Belt and braces: there is no password input on the create form, so the
      // key should not exist — and the server ignores it if it does. Deleting
      // it here means a stale browser bundle is the only thing that could ever
      // send one, and it says at the call site that this route does not take
      // one.
      if (!isEdit) delete payload.password;
      delete payload.group_name;
      // Where a staff block came from — output only, never an input.
      delete payload.billing_group_name;
      // Only a `user` has a billing identity of their own. An operator or group
      // admin is billed as their group and an admin as nobody, and the server
      // refuses a changed value for either (2026-09-28) — so none is posted.
      if (payload.role !== "user") {
        for (const [f] of BILLING_ADDRESS_FIELDS) delete payload[f];
        delete payload.vat_id;
      }
      const result = isEdit
        ? await api.patch(`/api/admin/users/${initial.id}`, payload)
        : await api.post("/api/admin/users", payload);
      onSave(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Only what this account may actually grant in the chosen group. The server
  // decides for real (can_grant); offering more here would just produce a 403
  // from a control that looked enabled.
  // Every role this account may grant somewhere; the group list below is then
  // narrowed to where the chosen role may be granted.
  const roleOptions = grantableAnywhere(me);
  const groupOptions = groupsForRole(me, form.role, myGroups);
  // On create there is no password to check — the account is given none. On
  // edit, a blank means "keep the current one", which is also nothing to check.
  const passwordOk = !isEdit || !form.password || passwordIsAcceptable(form.password, form.email);
  // A group is required for a staff role only: an admin sees them all, and a
  // `user` belongs to none (2026-09-28) — the server drops a group sent for
  // either. Create only: an account made before 2026-09-09 may hold its role
  // in no group, and gating the edit form on it would make it unsaveable.
  const groupOk = isEdit || !roleNeedsGroup(form.role) || String(form.group_id ?? "") !== "";
  // And where the customer IS, for the people a quotation is written to. Staff
  // accounts are not asked for one. Same reasoning about edit: an account that
  // predates the rule must stay saveable.
  //
  // **All five postal boxes** — a partial address is not an address, and half
  // of one on file reads as though somebody had checked. It used to be two
  // halves; the recipients list was the other one, and it went on 2026-09-21
  // because the portal issues no bill for it to be a recipient of.
  const filled = f => !!String(form[f] ?? "").trim();
  const addressOk = isEdit || form.role !== "user"
    || BILLING_ADDRESS_FIELDS.every(([f]) => filled(f));
  // The group's block is shown with its values only while it is still the
  // block the server answered with: the account was staff when opened and its
  // group is unchanged. After a promotion or a change of group the values are
  // not known here, and the block names the group rather than showing the
  // wrong one's address.
  const shownIsCurrent = isEdit && billedAsGroup(initial.role)
    && String(form.group_id ?? "") === String(initial.group_id ?? "");
  const billingGroupName = shownIsCurrent
    ? initial.billing_group_name
    : groups.find(g => String(g.id) === String(form.group_id ?? ""))?.name;

  return (
    <Card style={{ marginBottom: 24 }}>
      <h3 data-editor-heading tabIndex={-1} style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, outline: "none" }}>
        {isEdit ? "Edit User" : "New User"}
      </h3>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div data-form-grid style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {/* First, because it is what the account is known by — there is no
              username to ask for since 2026-09-28. */}
          <Field label="Email" required={!isEdit}
                 hint={isEdit ? undefined : "What they sign in with, and where the invitation is sent. There is no other way into the account, so it cannot be left out."}>
            <Input type="email" value={form.email} onChange={set("email")} placeholder="user@boku.ac.at" />
          </Field>
          {/* Two halves since 2026-09-16, both required. `full_name` is composed
              by the server as "surname given-name" — it is derived, and a third
              box somebody can type into is a third value that drifts from the
              pair it is supposed to be made of. */}
          <Field label="First name" required={!isEdit}>
            <Input value={form.first_name} onChange={set("first_name")} placeholder="Marie" />
          </Field>
          <Field label="Surname" required={!isEdit}>
            <Input value={form.last_name} onChange={set("last_name")} placeholder="Curie" />
          </Field>
          {/* Edit only. Creating an account no longer sets a password — the
              person sets their own from the link in their invitation — but
              changing an existing one is a different act, done by somebody who
              already holds the account, and it stays. */}
          {isEdit && (
            <Field label="New Password (leave blank to keep)">
              <PasswordInput value={form.password} onChange={set("password")} placeholder="••••••••" />
              <PasswordStrength password={form.password} />
              <PasswordRequirements password={form.password} identity={form.email} />
            </Field>
          )}
          <Field label="Role"
                 hint={roleOptions.length === 0 ? "You cannot grant any role." : undefined}>
            <Select
              options={roleOptions.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
              value={form.role}
              onChange={set("role")}
              disabled={roleOptions.length <= 1}
            />
          </Field>
          {/* Asked for a staff role only. An admin sees every group, and a
              `user` belongs to none — they reach a project by owning it, being
              assigned to it or representing it (2026-09-28: *users are
              associated with projects, not with groups*). The server drops a
              group sent for either, and `UserRole` refuses one on a `user`.
              Select renders its own empty "— select —" first, so there is
              nothing preselected to submit by accident. */}
          {roleNeedsGroup(form.role) && (
            <Field label="Group" required
                   hint={groupOptions.length === 0
                     ? `There is no group in which you may grant ${ROLE_LABELS[form.role]}.`
                     : `The group this ${ROLE_LABELS[form.role].toLowerCase()} works for.`}>
              {groupOptions.length === 1 ? (
                <Input value={groupOptions[0].name} disabled />
              ) : (
                <Select
                  options={groupOptions.map(g => ({ value: String(g.id), label: g.name }))}
                  value={String(form.group_id ?? "")}
                  onChange={set("group_id")}
                />
              )}
            </Field>
          )}
        </div>

        {/* Where the invoice goes. Its own block below the grid rather than two
            more cells in it, laid out the way the group profile lays out its
            Address section (`GroupManagementTab.jsx`, FIELDS) — five short
            boxes reading as one fact, not five unrelated ones.

            This is *Billing address* and takes a place. A second field
            called *Billing recipients* stood above it and took e-mail, and the
            two were both called *Invoicing address* until 2026-09-14 — which is
            how a facility came to be shown `invoices@example.org` as the
            example of where to write a street. The e-mail one went on
            2026-09-21: the record of finished work is addressed from the GROUP,
            not from the person the work was for. The rule that survives it is
            that no two fields here may share a name. */}
        {billedAsGroup(form.role) && (
          <GroupBillingBlock account={shownIsCurrent ? initial : null} groupName={billingGroupName} />
        )}
        {form.role === "user" && (
          <section aria-labelledby="user-form-billing-heading">
            <h4 id="user-form-billing-heading"
                style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 600, color: "#374151",
                         letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Billing address
            </h4>
            <p style={{ margin: "0 0 10px", fontSize: 11, color: "#6b7280" }}>
              Who the customer is — the institution or company the quotation is issued to, and
              the address whoever writes the invoice needs.
              {!isEdit && " All five are required for a researcher account."} The VAT id is optional.
            </p>
            {/* VAT under the address since 2026-09-28 (the maintainer: *move it
                in the form next to the billing address*): it is the sixth line
                of the same identity, and the one that is optional. It stood in
                the grid above, beside the role, until then. The profile form a
                self-registered person fills in has the same block, in the same
                order (`AccountCard.jsx`, BILLING_LINES). */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
              {BILLING_ADDRESS_FIELDS.map(([field, label]) => (
                <Field key={field} label={label} required={!isEdit}>
                  <Input value={form[field] ?? ""} onChange={set(field)} />
                </Field>
              ))}
              <Field label="UID / VAT ID" hint="The tax id the invoice is issued under. Optional.">
                <Input value={form.vat_id ?? ""} onChange={set("vat_id")} placeholder="ATU…" />
              </Field>
            </div>
          </section>
        )}

        {/* Not a choice any more, and the toggle is shown switched on and
            locked rather than removed, so it is clear that the message goes and
            that nobody forgot to tick it. Its old copy — "The password you
            typed above is never emailed" — described a password that no longer
            exists; what the message carries is a one-time link the person
            follows to set their own. Turning it off would create an account
            with no password and no way to be told about it, which is an account
            nobody can ever sign in to. If it does not arrive, Send invitation
            on their row does it again with a fresh link. */}
        {!isEdit && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 14px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
            <div>
              <div style={{ color: "#1a1a1a", fontSize: 13 }}>Send welcome email</div>
              <div style={{ color: "#6b7280", fontSize: 11, maxWidth: 520 }}>
                The account is created without a password. This message is the only way into it:
                it carries a one-time link the person follows to set their own. It is always sent,
                and you can send it again from their row if it does not arrive.
              </div>
            </div>
            <Toggle
              checked={true}
              disabled={true}
              label="Send welcome email"
              onChange={() => {}}
            />
          </div>
        )}

        {!isEdit && form.email && (
          <MessagePreview
            title="What they will receive"
            deps={["welcome"]}
            fetcher={() => api.get("/api/admin/account-email-preview?kind=welcome")}
          />
        )}

        {error && (
          <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
          <Btn type="submit" disabled={busy || !passwordOk || !groupOk || !addressOk || (!isEdit && (!form.email || !form.first_name || !form.last_name))}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
          </Btn>
        </div>
      </form>
    </Card>
  );
}

// ── Group form ────────────────────────────────────────────────────────────────

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminUsersPage() {
  const { user: me, refreshUser } = useAuth();
  const isAdmin               = me?.is_admin;
  // A group admin sees the Group management tab for the groups they administer.
  // They cannot call /api/admin/groups (admin only) and do not need to: their
  // own grants already name every group they may touch, and its name.
  const myAdminGroups = managedGroups(me, "group_admin");
  const [users, setUsers]     = useState([]);
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);
  // { user, preview } — the preview is fetched before the dialog opens, so the
  // reasons a deletion is refused are on screen before the click, not after it.
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // The edit form and the row-action messages render at the TOP of the page,
  // and on a phone a row of the table is a card: tapping Edit on the fifth
  // account changed something thousands of pixels above the finger and nothing
  // in view (2026-10-20). Each is scrolled to when it appears.
  const editorRef = useRef(null);
  const noticeRef = useRef(null);
  const errorRef  = useRef(null);
  // The account whose Edit opened the form, so focus — and with it the scroll
  // position — goes back to that row when the form closes.
  const editedFrom = useRef(null);
  // The id of the account whose invitation is in flight, so only that row's
  // button says "Sending…" — a page-wide busy flag would disable every row.
  const [inviting, setInviting] = useState(null);
  // The Keycloak account being switched back to local, while its confirmation
  // is open.
  const [switching, setSwitching] = useState(null);
  const [switchBusy, setSwitchBusy] = useState(false);
  // The id of the account whose confirmation is in flight.
  const [confirming, setConfirming] = useState(null);
  const tz = useDisplayTimezone();

  const userAccessors = useMemo(() => ({
    email:     u => u.email || "",
    full_name: u => u.full_name || "",
    role:      u => ROLE_LABELS[u.role] || u.role,
    group:     u => isResearcher(u)
      ? (u.projects || []).map(p => p.name).join(", ")
      : groups.find(g => g.id === u.group_id)?.name || "",
    auth:      u => u.auth_source,
    // Sorted on the instant, not on what it is painted as: "" sorts a never-
    // invited account to one end rather than under the letter F of "Feb".
    invited:   u => u.invitation_sent_at || "",
    status:    u => u.is_active ? 1 : 0,
  }), [groups]);
  const { sorted: sortedUsers, sortKey: userSortKey, sortDir: userSortDir, toggleSort: toggleUserSort } = useSortableColumns(users, userAccessors);
  const { widths: userWidths, setWidth: setUserWidth, totalWidth: userTotalWidth } = useColumnWidths({
    // `actions` fits four buttons on a plain local account: Edit, Send
    // invitation, Deactivate and Delete.
    email: 210, full_name: 160, role: 110, group: 150, auth: 130, invited: 170, status: 110, actions: 480,
  });

  const loadUsers = () => api.get("/api/admin/users").then(setUsers).catch(e => setError(e.message));
  const loadGroups = () => isAdmin ? api.get("/api/admin/groups").then(setGroups).catch(e => setError(e.message)) : Promise.resolve();

  const loadAll = () => {
    setLoading(true);
    Promise.all([loadUsers(), loadGroups()])
      .then(() => setError(null))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // `?.` on scrollIntoView: jsdom does not implement it.
  const editingKey = editing ? (editing.id ?? "new") : null;
  useEffect(() => {
    if (editingKey === null) return;
    editorRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    // The form's heading, not its first field: focusing an input opens the
    // keyboard on a phone before the person has seen which account this is.
    editorRef.current?.querySelector("[data-editor-heading]")?.focus({ preventScroll: true });
  }, [editingKey]);
  useEffect(() => {
    if (notice) noticeRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [notice]);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [error]);
  // Back to the row once the form is closed and the table is on screen again
  // — after a save `loadAll` replaces it with "Loading…" for a moment.
  useEffect(() => {
    if (editing || loading || editedFrom.current == null) return;
    const id = editedFrom.current;
    editedFrom.current = null;
    document.querySelector(`[data-user-id="${id}"] button`)?.focus();
  }, [editing, loading]);

  const openEditor = u => {
    editedFrom.current = u?.id ?? null;
    setEditing(u);
  };

  const handleSaved = result => {
    setEditing(null);
    loadAll();
    // The account exists either way — creation is deliberately not rolled back
    // over a mail server. But the account now has NO password, so a message
    // that did not go out is the difference between a working account and one
    // nobody can ever sign in to, and the operator has to know that now.
    if (result?.email_sent === true) {
      setNotice(`Account created. An invitation was sent to ${result.email}; they set their own password from the link in it.`);
    } else if (result?.email_sent === false) {
      setNotice(`Account created, but the invitation could not be sent (${result.email_error}). The account has no password until somebody follows that link — use "Send invitation" on their row to try again.`);
    } else {
      setNotice(null);
    }
  };

  // The way out of a mail that did not arrive. A fresh link every time: a token
  // lives 30 minutes, so re-mailing the old one would be theatre.
  const handleInvite = async u => {
    setInviting(u.id);
    setError(null);
    try {
      const result = await api.post(`/api/admin/users/${u.id}/send-invitation`);
      if (result?.email_sent === false) {
        setError(`The invitation to ${u.email} could not be sent (${result.email_error}).`);
      } else {
        setNotice(`An invitation was sent to ${result?.email || u.email}. The link lets them set their own password.`);
      }
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(null);
    }
  };

  const handleLocalSignIn = async () => {
    const u = switching;
    setSwitchBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/users/${u.id}/local-sign-in`);
      setNotice(`${u.email} now signs in locally. The account has no password yet — use "Send invitation" on their row so they can set one.`);
      loadUsers();
    } catch (err) {
      // 409 when the account is already local or deactivated, 403 on the
      // caller's own: the server's sentence says which.
      setError(err.message);
    } finally {
      setSwitchBusy(false);
      setSwitching(null);
    }
  };

  // `submitted → confirmed` (2026-09-28): an operator vouches for the details a
  // self-registered person stated. The confirmation is committed before the
  // mail is tried, so a mail that did not go out is said beside a confirmation
  // that stands — the shape the invitation reports its own send in.
  const handleConfirm = async u => {
    setConfirming(u.id);
    setError(null);
    try {
      const result = await api.post(`/api/admin/users/${u.id}/confirm-profile`);
      const who = u.full_name || u.email;
      setNotice(result?.email_sent === false
        ? `${who}'s profile is confirmed, but the e-mail telling them could not be sent (${result.email_error}).`
        : `${who}'s profile is confirmed, and they were told by e-mail.`);
      loadUsers();
    } catch (err) {
      // 409 when it is not submitted (any more), 422 naming what is missing.
      setError(err.message);
    } finally {
      setConfirming(null);
    }
  };

  const handleToggle = async u => {
    try {
      await api.patch(`/api/admin/users/${u.id}`, { is_active: !u.is_active });
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const openDelete = async u => {
    setError(null);
    try {
      setDeleting({ user: u, preview: await api.get(`/api/admin/users/${u.id}/deletion-preview`) });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/api/admin/users/${deleting.user.id}`);
      setDeleting(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  // Which roles get a card, and what each one is scoped to.
  const myRoles = manageableRoles(grantsOf(me));

  // The rank, as a dot. ROLE_COLORS names Badge's palette; this needs a
  // literal colour, and the two are deliberately not the same thing.
  const ROLE_BADGE_COLOR = {
    admin: "#b45309", group_admin: "#7c3aed", operator: "#2563eb", user: "#6b7280",
  };

  const roleScopeLabel = role => {
    if (role === "admin") return "every group";
    const named = grantsOf(me)
      .filter(g => g.role === role && g.group_id != null)
      .map(g => g.group_name || `Group ${g.group_id}`);
    return named.length ? named.join(", ") : null;
  };

  // Self-registered accounts the facility has not vouched for yet, oldest
  // first. An `incomplete` one is listed so an operator can see it exists, but
  // offered no Confirm: the person has not handed their details in, and the
  // server answers 409.
  const awaiting = users
    .filter(u => !profileConfirmed(u))
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));

  const awaitingQueue = awaiting.length > 0 && (
    <Card style={{ marginBottom: 16 }}>
      <section aria-labelledby="awaiting-confirmation-heading">
        <h3 id="awaiting-confirmation-heading" style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>
          Awaiting confirmation ({awaiting.length})
        </h3>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "#6b7280" }}>
          People who registered themselves through the sign-in service. Until an operator confirms
          the details they stated, their account reaches only its own profile. They are on no
          project yet, so any operator may correct a detail with Edit first, then confirm.
        </p>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {awaiting.map(u => (
            <li key={u.id} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
                                    padding: "8px 12px", borderRadius: 8, background: "#f9fafb",
                                    border: "1px solid #e5e7eb", fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{u.full_name || u.email}</span>
              <span style={{ color: "#4b5563" }}>{u.email || "no e-mail address"}</span>
              {u.billing_name && <span style={{ color: "#4b5563" }}>{u.billing_name}</span>}
              <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                {u.profile_state === "submitted" ? (
                  <>
                    <Btn variant="secondary" onClick={() => openEditor(u)}
                         style={{ padding: "5px 12px", fontSize: 12 }}>Edit</Btn>
                    <Btn onClick={() => handleConfirm(u)} disabled={confirming === u.id}
                         style={{ padding: "5px 12px", fontSize: 12 }}>
                      {confirming === u.id ? "Confirming…" : "Confirm"}
                    </Btn>
                  </>
                ) : (
                  <span style={{ color: "#6b7280" }}>Still completing their details</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </Card>
  );

  const usersPanel = (
    <>
    {awaitingQueue}
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: userTotalWidth }}>
          <thead>
            <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <Th label="Email" sortKey="email" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.email} onWidth={w => setUserWidth("email", w)} />
              <Th label="Full Name" sortKey="full_name" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.full_name} onWidth={w => setUserWidth("full_name", w)} />
              <Th label="Role" sortKey="role" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.role} onWidth={w => setUserWidth("role", w)} />
              <Th label="Group / Projects" sortKey="group" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.group} onWidth={w => setUserWidth("group", w)} />
              <Th label="Auth" sortKey="auth" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.auth} onWidth={w => setUserWidth("auth", w)} />
              {/* The column is read back out of the audit log — there is no
                  `invited_at` on the account — so clearing a group's log, or
                  letting log retention age it out, takes the sends with it and
                  every account in that group reads Never. Said on the header
                  because what an operator does next with this column is decide
                  NOT to press "Send invitation" again. */}
              <Th
                label={<span title={INVITED_NOTE}>Invited<span aria-hidden="true" style={{ marginLeft: 3, opacity: 0.6 }}>&#9432;</span></span>}
                sortKey="invited" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.invited} onWidth={w => setUserWidth("invited", w)} />
              <Th label="Status" sortKey="status" activeKey={userSortKey} dir={userSortDir} onSort={toggleUserSort} width={userWidths.status} onWidth={w => setUserWidth("status", w)} />
              <Th label="" align="right" width={userWidths.actions} onWidth={w => setUserWidth("actions", w)} />
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 && (
              <tr><td colSpan={9} style={{ padding: "32px 16px", textAlign: "center", color: "#6b7280", fontSize: 14 }}>No users found</td></tr>
            )}
            {sortedUsers.map(u => (
              <UserRow
                key={u.id}
                user={u}
                currentUserId={me?.id}
                groups={groups}
                onEdit={openEditor}
                tz={tz}
                inviting={inviting === u.id}
                onToggle={handleToggle}
                onDelete={openDelete}
                onInvite={handleInvite}
                onLocalSignIn={setSwitching}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
    </>
  );

  // One section, rendered for the role whose card it is sitting in.
  //
  // `role` is passed even where it is not read yet: it is what stops a section
  // silently becoming the union of every role that shows it. `quotations` and
  // `groupManage` are the two cases that read it today.
  //
  // It used to say `users` read it, and `users` does not: `usersPanel` is a
  // role-independent const, and the roles a card may grant come from
  // `grantableAnywhere(me)` and `groupsForRole` inside `UserForm`, OUTSIDE every
  // card. So the comment arguing this pattern was already safe was itself the
  // evidence nobody had checked it — which is exactly how `quotations` came to
  // read the page-level `isAdmin` while being handed the card's role.
  const renderSection = (key, role) => {
    switch (key) {
      case "users":         return usersPanel;
      // **`refreshUser` here and not in `loadAll`** (2026-09-16). `delete_group`
      // deletes the grants held in the group it removes, server-side, so an
      // account holding group_admin in the group it has just deleted keeps that
      // grant in `me` until the page is reloaded. `myAdminGroups` is derived
      // from `me`, so the group-admin card's picker went on offering a group
      // that no longer exists — and choosing it sent `?group_id=<gone>`, which
      // `_stats_scope` answers with `sorted(managed)[0]`: somebody else's group.
      // Reloading users and groups cannot fix that; neither is where the stale
      // grant lives.
      //
      // Not folded into `loadAll`, which runs after every save on this page:
      // re-reading `/auth/me` because somebody ELSE was granted a role is a
      // request for nothing, and `AdminUsersPage.test.jsx` pins that it does not
      // happen.
      case "groups":        return <GroupsTab groups={groups} onGroupsChange={() => { loadAll(); refreshUser?.(); }} />;
      // Scoped by THIS CARD's role — the same treatment as `quotations` below,
      // and for the same reason. `groups` still comes from the page, because a
      // group admin may not call /api/admin/groups at all and their own grants
      // are where the groups they administer are named.
      //
      // `role` is handed on as well as used here: the panel decides for itself
      // whether each group's settings may be EDITED, and it used to decide that
      // from `isGlobalAdmin(me)` — which is true in the group-admin card of an
      // account that is also an admin. Narrowing the list without telling the
      // panel which card it is in leaves that standing.
      case "groupManage": {
        const cardIsAdmin = role === "admin";
        return <GroupManagementTab me={me} role={role}
                                   groups={cardIsAdmin ? groups : myAdminGroups} />;
      }
      case "documents":     return <AdminDocumentsPanel groups={groups} />;
      // Scoped by THIS CARD's role, not by the page-level `isAdmin`: an account
      // holding admin and group_admin gets the facility-wide picker in the
      // Admin card and only the groups it administers in the group-admin card.
      // A card must never widen what a role can do, and the file that comes out
      // of this one may not be wider than the heading above it.
      //
      // `auth/noInlineRoles.test.js` does not flag `role === "admin"` because it
      // matches a member expression and `role` is a plain identifier. That is
      // correct here rather than a gap in the ratchet: the question being asked
      // is which card this is, not what the signed-in account happens to hold.
      // Do not "fix" it back to `isAdmin` or to `me.role`.
      case "quotations": {
        const cardIsAdmin = role === "admin";
        return <QuotationsExportPanel groups={cardIsAdmin ? groups : myAdminGroups}
                                      isAdmin={cardIsAdmin} />;
      }
      case "notifications": return <NotificationsTab />;
      case "signIn":        return <SignInCard />;
      case "announce":      return <AnnouncementsTab groups={groups} users={users} />;
      case "automation":    return <AutomationTab />;
      case "backup":        return <BackupCard scope="full" />;
      case "settings":      return <SettingsTab />;
      default:              return null;
    }
  };


  return (
    <PortalLayout>
      <div data-page-header style={{ display: "flex", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          {/* "Management", not "Users": this page holds eleven sections — accounts,
              groups, facility settings, documents, the quotations export,
              e-mail, sign-in, announcements, automation, backup and portal
              settings — and naming it after one of them hid the other ten. The /admin/users route is unchanged:
              renaming it would break bookmarks and no user ever sees it.

              They are collapsible sections inside a card per role, not tabs.
              `auth/managementSections.js` is the list, and it is data so that a
              new section cannot arrive under the wrong gate. */}
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            {isAdmin ? "Management" : "Team Management"}
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: "#6b7280" }}>
            {users.length} user{users.length !== 1 ? "s" : ""}
            {isAdmin && groups.length > 0 && ` · ${groups.length} group${groups.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <div data-page-actions style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {!editing && (
            <Btn onClick={() => setEditing("new")}>+ New User</Btn>
          )}
        </div>
      </div>

      <div ref={editorRef} style={{ scrollMarginTop: 72 }}>
      {editing && editing.id && (
        <Card style={{ marginBottom: 20 }}>
          <RoleEditor me={me} user={editing} groups={groups}
                      onChange={u => {
                        setEditing(u); loadAll();
                        // Only when you changed your OWN standing — the rest of
                        // the page already reloads. `?.` because every test in
                        // the tree mocks useAuth as `{ user }`.
                        if (u.id === me?.id) refreshUser?.();
                      }} />
        </Card>
      )}

      {editing && (
        <UserForm
          initial={editing === "new" ? null : editing}
          me={me}
          groups={groups}
          onSave={handleSaved}
          onCancel={() => setEditing(null)}
        />
      )}
      </div>

      {notice && (
        <div role="status" ref={noticeRef} style={{ scrollMarginTop: 72, padding: "12px 16px", borderRadius: 8, background: "#f3f4f6", border: "1px solid #d1d5db", color: "#374151", fontSize: 13, marginBottom: 20, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ flex: 1 }}>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss message"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 16, lineHeight: 1, padding: "0 4px" }}>×</button>
        </div>
      )}

      {error && (
        <div role="alert" ref={errorRef} style={{ scrollMarginTop: 72, padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* One card per role this account holds, strongest first.
          Replaces a flat tab strip whose every button carried its own
          `isAdmin &&`: an account with several roles could not tell which power
          came from which, and there was nothing to stop a new tab arriving
          under the wrong gate. What each role shows is data now, in
          auth/managementSections.js.

          A card never widens what a role may do. The limits still come from
          `grantableAnywhere` / `groupsForRole` and, above all, from the server — this
          decides what is SHOWN. */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}>Loading…</div>
      ) : myRoles.length === 0 ? (
        <Card>
          <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
            Your account holds no role that manages anything.
          </p>
        </Card>
      ) : (
        myRoles.map((role, i) => (
          <Collapsible
            key={role}
            title={ROLE_LABELS[role] || role}
            /* No badge repeating the title — the card is named after the role,
               and "Admin Admin every group" is what putting both gave. The
               colour still carries the rank. */
            badge={<span aria-hidden="true"
                         style={{ width: 8, height: 8, borderRadius: 4,
                                  background: ROLE_BADGE_COLOR[role] || "#9ca3af" }} />}
            subtitle={roleScopeLabel(role)}
            /* The strongest role open by default: it is what somebody arriving
               here is most likely to have come for, and one open card is a
               clearer starting point than several closed ones. */
            defaultOpen={i === 0}
          >
            {sectionsForRole(role).map((section, j) => (
              <Collapsible key={section.key} title={section.label} level={1}
                           /* The first section of the open card, so arriving
                              here still lands on something — for every role
                              that is Users, which is what the page was named
                              after and what people come for. */
                           defaultOpen={i === 0 && j === 0}>
                {renderSection(section.key, role)}
              </Collapsible>
            ))}
          </Collapsible>
        ))
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete user "${deleting.user.email}"?`}
          confirmWord={deleting.user.email}
          confirmLabel="Delete account"
          busy={deleteBusy}
          blockers={deleting.preview.blockers}
          onConfirm={handleDelete}
          onClose={() => setDeleting(null)}
        >
          <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151" }}>
            The account is removed for good. Deactivating it instead keeps it, and keeps the person
            out.
          </p>
          <KeptSummary preview={deleting.preview} />
        </ConfirmDialog>
      )}

      {switching && (
        <ConfirmDialog
          title={`Switch "${switching.email}" back to local sign-in?`}
          confirmLabel="Switch to local"
          tone="primary"
          busy={switchBusy}
          onConfirm={handleLocalSignIn}
          onClose={() => setSwitching(null)}
        >
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            The account is unlinked from its Keycloak identity and signs in with a portal password
            again. It has no password until one is set: use "Send invitation" on its row afterwards —
            the link in it lets the person choose their own.
          </p>
        </ConfirmDialog>
      )}
    </PortalLayout>
  );
}

// What survives the deletion, spelled out. These are counts, not obstacles —
// they must never be passed to ConfirmDialog as blockers, or the confirm button
// would be locked for every account that has ever done anything.
function KeptSummary({ preview }) {
  const kept = [
    [preview.submissions, "submission"],
    [preview.results,     "delivery"],
    [preview.jobs,        "job"],
  ].filter(([n]) => n > 0);

  if (!kept.length && !preview.memberships) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
        This account has no submissions, deliveries or project assignments.
      </p>
    );
  }
  return (
    <div style={{ padding: "12px 14px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb", fontSize: 13, color: "#374151" }}>
      {kept.length > 0 && (
        <p style={{ margin: 0 }}>
          <strong>{kept.map(([n, word]) => `${n} ${word}${n === 1 ? "" : "s"}`).join(", ")}</strong>{" "}
          will be kept and stay listed under their projects — only the link to this account is
          dropped, so they will no longer show a submitter. The audit log keeps the name.
        </p>
      )}
      {preview.memberships > 0 && (
        <p style={{ margin: kept.length ? "8px 0 0" : 0 }}>
          Access to {preview.memberships} project{preview.memberships === 1 ? "" : "s"} is withdrawn.
        </p>
      )}
    </div>
  );
}
